import {
  HeadingFieldType,
  HtmlFieldType,
  LargeInputFieldType,
  SmallInputFieldType,
  SubheadingFieldType,
} from '@components/form/v2';
import { MenuItemProps } from '@components/menu/menu';
import { useScopedTranslate } from '@locale/translate';
import { getRawTextFromHtml } from '@utils/browser';

import { dropdownConfig } from './dropdown';
import { headingConfig } from './heading';
import { htmlConfig } from './html';
import { multipleChoiceConfig } from './multiple-choice';
import { singleChoiceConfig } from './single-choice';
import { subheadingConfig } from './subheading';
import { textLargeField } from './text-large';
import { textSmallField } from './text-small';

import { useFormBuilderContext } from '../context';
import { capShortTextValue } from '../default-value-mapping';
import { kindExistsInRegistry } from '../field-registry';
import { FieldContainingOptions } from '../state';

const actionsLocale = {
  en: {
    convert: 'Convert to {0}',
  },
};

export const useOptionsFieldActions = (field: FieldContainingOptions): MenuItemProps[] => {
  const actions = useFormBuilderContext().store.actions;
  const { t } = useScopedTranslate(actionsLocale);

  let opts: Array<MenuItemProps> = [];

  const canEditKind = field.builderProps?.canEditKind;
  if (canEditKind === false) {
    return opts;
  }

  const allowedKinds = Array.isArray(canEditKind) ? canEditKind : undefined;

  const sourceIsMulti = field.kind === 'input-multiple-choice';
  const stringDefault: string | null = sourceIsMulti
    ? (field.defaultValue as string[] | undefined)?.[0] ?? null
    : (field.defaultValue as string | null | undefined) ?? null;
  const arrayDefault: string[] = sourceIsMulti
    ? (field.defaultValue as string[] | undefined) ?? []
    : stringDefault
    ? [stringDefault]
    : [];

  const sharedOverrides = {
    label: field.label,
    isRequired: field.isRequired,
    options: field.options,
    optionsFromListId: field.optionsFromListId,
    excludedOptions: field.excludedOptions,
    journeyAttributeId: field.journeyAttributeId,
    companyAttributeId: field.companyAttributeId,
    builderProps: field.builderProps,
  };

  if (
    kindExistsInRegistry('input-single-choice') &&
    (!allowedKinds || allowedKinds.includes('input-single-choice'))
  ) {
    opts.push({
      label: t('convert', singleChoiceConfig.title),
      icon: <singleChoiceConfig.Icon />,
      isDisabled: field.kind === 'input-single-choice',
      onSelect: () => {
        actions.changeFieldKind(field.id, 'input-single-choice', {
          ...sharedOverrides,
          defaultValue: stringDefault,
          mappedDefaultValue: field.mappedDefaultValue,
        });
      },
    });
  }

  if (
    kindExistsInRegistry('input-multiple-choice') &&
    (!allowedKinds || allowedKinds.includes('input-multiple-choice'))
  ) {
    opts.push({
      label: t('convert', multipleChoiceConfig.title),
      icon: <multipleChoiceConfig.Icon />,
      isDisabled: field.kind === 'input-multiple-choice',
      onSelect: () => {
        actions.changeFieldKind(field.id, 'input-multiple-choice', {
          ...sharedOverrides,
          defaultValue: arrayDefault,
          mappedDefaultValue: field.mappedDefaultValue,
        });
      },
    });
  }

  if (
    kindExistsInRegistry('input-dropdown') &&
    (!allowedKinds || allowedKinds.includes('input-dropdown'))
  ) {
    opts.push({
      label: t('convert', dropdownConfig.title),
      icon: <dropdownConfig.Icon />,
      isDisabled: field.kind === 'input-dropdown',
      onSelect: () => {
        actions.changeFieldKind(field.id, 'input-dropdown', {
          ...sharedOverrides,
          defaultValue: stringDefault,
          mappedDefaultValue: field.mappedDefaultValue,
        });
      },
    });
  }

  return opts;
};

export const useTextInputActions = (field: SmallInputFieldType | LargeInputFieldType) => {
  const actions = useFormBuilderContext().store.actions;
  const { t } = useScopedTranslate(actionsLocale);

  const canEditKind = field.builderProps?.canEditKind;
  if (canEditKind === false) {
    return [];
  }

  const allowedKinds = Array.isArray(canEditKind) ? canEditKind : undefined;
  let opts: Array<MenuItemProps> = [];

  const sharedTextOverrides = {
    label: field.label,
    isRequired: field.isRequired,
    condition: field.condition,
    placeholder: field.placeholder,
    journeyAttributeId: field.journeyAttributeId,
    companyAttributeId: field.companyAttributeId,
    builderProps: field.builderProps,
    mappedDefaultValue: field.mappedDefaultValue,
  };

  if (
    kindExistsInRegistry('input-text-small') &&
    (!allowedKinds || allowedKinds.includes('input-text-small'))
  ) {
    opts.push({
      label: t('convert', textSmallField.title),
      icon: <textSmallField.Icon />,
      isDisabled: field.kind === 'input-text-small',
      onSelect: () => {
        actions.changeFieldKind(field.id, 'input-text-small', {
          ...sharedTextOverrides,
          mappedDefaultValue: field.mappedDefaultValue
            ? capShortTextValue(field.mappedDefaultValue)
            : undefined,
        });
      },
    });
  }

  if (
    kindExistsInRegistry('input-text-large') &&
    (!allowedKinds || allowedKinds.includes('input-text-large'))
  ) {
    opts.push({
      label: t('convert', textLargeField.title),
      icon: <textLargeField.Icon />,
      isDisabled: field.kind === 'input-text-large',
      onSelect: () => {
        actions.changeFieldKind(field.id, 'input-text-large', sharedTextOverrides);
      },
    });
  }

  return opts;
};

export const useTextContentActions = (
  field: HeadingFieldType | SubheadingFieldType | HtmlFieldType
) => {
  const actions = useFormBuilderContext().store.actions;
  const { t } = useScopedTranslate(actionsLocale);

  let opts: Array<MenuItemProps> = [];

  if (kindExistsInRegistry('heading')) {
    opts.push({
      isDisabled: field.kind === 'heading',
      label: t('convert', headingConfig.title),
      icon: <headingConfig.Icon />,
      onSelect: () => {
        actions.changeFieldKind(field.id, 'heading', {
          label:
            'label' in field
              ? field.label
              : 'content' in field
              ? getRawTextFromHtml(
                  typeof field.content === 'string' ? field.content : field.content.value
                )
              : '',
        });
      },
    });
  }

  if (kindExistsInRegistry('subheading')) {
    opts.push({
      isDisabled: field.kind === 'subheading',
      label: t('convert', subheadingConfig.title),
      icon: <subheadingConfig.Icon />,
      onSelect: () => {
        actions.changeFieldKind(field.id, 'subheading', {
          label:
            'label' in field
              ? field.label
              : 'content' in field
              ? getRawTextFromHtml(
                  typeof field.content === 'string' ? field.content : field.content.value
                )
              : '',
        });
      },
    });
  }

  if (kindExistsInRegistry('html')) {
    opts.push({
      isDisabled: field.kind === 'html',
      label: t('convert', htmlConfig.title),
      icon: <htmlConfig.Icon />,
      onSelect: () => {
        const labelValue =
          'label' in field
            ? typeof field.label === 'string'
              ? field.label
              : field.label?.value ?? ''
            : '';
        actions.changeFieldKind(field.id, 'html', {
          content: `<p>${labelValue}</p>`,
        });
      },
    });
  }

  return opts;
};
